import { useEffect, useMemo, useState } from 'react'
import { Quintenzirkel } from '../components/Quintenzirkel'
import { Klaviatur } from '../components/Klaviatur'
import { kadenz } from '../domain/audio'
import {
  DUR_TONARTEN, dominante, subdominante,
} from '../domain/tonarten'
import { alleUebungsabschnitte, findeUebungsabschnitt } from '../curriculum/quintenzirkel'
import { neueKarte, bewerteKarte } from '../engine/fsrs'
import {
  holeOderErzeugeKarte, speichereKarte, protokolliereEvent, ladeAlleKarten,
} from '../store/db'
import type {
  Aufgabe, EvaluationEvent, FsrsKarte, Mode, Rating, Tonart,
} from '../types'

interface Props {
  modus: Mode
}

interface SessionEintrag {
  aufgabe: Aufgabe
  korrekt: boolean
  antwort: string
}

function bewerte(aufgabe: Aufgabe, antwort: string): boolean {
  return aufgabe.richtigeAntwort.trim().toLowerCase() === antwort.trim().toLowerCase()
}

function ratingAusErgebnis(korrekt: boolean, dauerMs: number, hilfeGenutzt: boolean): Rating {
  if (!korrekt) return 1
  if (hilfeGenutzt) return 2
  if (dauerMs < 4000) return 4
  if (dauerMs < 10000) return 3
  return 2
}

export function UebenPage({ modus }: Props) {
  const [aufgabe, setAufgabe] = useState<Aufgabe | null>(null)
  const [start, setStart]     = useState<number>(0)
  const [hilfeGenutzt, setHilfeGenutzt] = useState(false)
  const [feedback, setFeedback] = useState<'pending' | 'richtig' | 'falsch'>('pending')
  const [letzteEingabe, setLetzteEingabe] = useState('')
  const [session, setSession] = useState<SessionEintrag[]>([])
  const [karten, setKarten]   = useState<FsrsKarte[]>([])

  const istVersteckt = modus === 'ueben-ohne-hilfe' && feedback === 'pending'
  const aktuelleTonart: Tonart | null = useMemo(() => {
    if (!aufgabe) return null
    const spec = aufgabe.spec
    if ('tonart' in spec) return spec.tonart
    return null
  }, [aufgabe])

  useEffect(() => {
    void ladeAlleKarten().then(setKarten)
    waehleNaechsteAufgabe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function waehleNaechsteAufgabe() {
    const abschnitte = alleUebungsabschnitte()
    const zufall = abschnitte[Math.floor(Math.random() * abschnitte.length)]
    setAufgabe(zufall.generateAufgabe())
    setStart(performance.now())
    setHilfeGenutzt(false)
    setFeedback('pending')
    setLetzteEingabe('')
  }

  async function antworteMit(antwort: string) {
    if (!aufgabe) return
    const korrekt = bewerte(aufgabe, antwort)
    const dauerMs = performance.now() - start
    setLetzteEingabe(antwort)
    setFeedback(korrekt ? 'richtig' : 'falsch')
    setSession(prev => [...prev, { aufgabe, korrekt, antwort }])

    const event: EvaluationEvent = {
      uebungsabschnittId: aufgabe.uebungsabschnittId,
      zeitpunkt: Date.now(),
      antwort,
      korrekt,
      dauerMs,
      hilfeGenutzt,
      modus,
    }
    await protokolliereEvent(event)

    const karte = await holeOderErzeugeKarte(
      aufgabe.uebungsabschnittId,
      () => neueKarte(aufgabe.uebungsabschnittId),
    )
    const rating = ratingAusErgebnis(korrekt, dauerMs, hilfeGenutzt)
    const aktualisiert = bewerteKarte(karte, rating)
    await speichereKarte(aktualisiert)
    setKarten(await ladeAlleKarten())
  }

  if (!aufgabe) return <div className="page laden">Lädt …</div>

  return (
    <div className={`page ueben ${modus}`}>
      <header className="page-kopf">
        <h1>{modus === 'ueben-mit-hilfe' ? 'Üben mit Hilfe' : 'Üben ohne Hilfe'}</h1>
        <p className="aufgabe-text">{aufgabe.frage}</p>
      </header>

      <div className="layout-zwei-spalten">
        <Quintenzirkel
          ausgewaehlt={aktuelleTonart}
          versteckt={istVersteckt}
          onWaehle={istVersteckt ? undefined : (t) => antworteMit(t.name)}
          highlightedDom={modus === 'ueben-mit-hilfe' && aktuelleTonart ? dominante(aktuelleTonart) : null}
          highlightedSub={modus === 'ueben-mit-hilfe' && aktuelleTonart ? subdominante(aktuelleTonart) : null}
        />
        <AntwortEingabe
          aufgabe={aufgabe}
          modus={modus}
          istVersteckt={istVersteckt}
          onAntwort={antworteMit}
          onHilfe={() => setHilfeGenutzt(true)}
          feedback={feedback}
          letzteEingabe={letzteEingabe}
          istHilfeGenutzt={hilfeGenutzt}
        />
      </div>

      <Klaviatur
        hervorhebung={modus === 'ueben-mit-hilfe' ? aktuelleTonart : null}
        onKlick={(midi) => {
          const spec = aufgabe.spec
          if (spec.typ === 'tonika-spielen' && feedback === 'pending') {
            const erwartet = spec.tonart.tonicMidi % 12
            const richtig = midi % 12 === erwartet
            antworteMit(richtig ? spec.tonart.name : `MIDI ${midi}`)
          }
        }}
      />

      {feedback !== 'pending' && (
        <div className={`feedback ${feedback}`}>
          {feedback === 'richtig'
            ? <>✓ Richtig! Antwort: <strong>{aufgabe.richtigeAntwort}</strong></>
            : <>✗ Falsch. Du hast geantwortet: <em>{letzteEingabe || '—'}</em>. Richtige Antwort: <strong>{aufgabe.richtigeAntwort}</strong></>}
          <button onClick={waehleNaechsteAufgabe}>Nächste Aufgabe</button>
        </div>
      )}

      <SessionStatistik session={session} karten={karten} />
    </div>
  )
}

interface EingabeProps {
  aufgabe: Aufgabe
  modus: Mode
  istVersteckt: boolean
  onAntwort: (antwort: string) => void
  onHilfe: () => void
  feedback: 'pending' | 'richtig' | 'falsch'
  letzteEingabe: string
  istHilfeGenutzt: boolean
}

function AntwortEingabe({
  aufgabe, modus, istVersteckt, onAntwort, onHilfe, feedback, istHilfeGenutzt,
}: EingabeProps) {
  const [text, setText] = useState('')
  const spec = aufgabe.spec

  if (spec.typ === 'tonart-im-kreis' && modus === 'ueben-mit-hilfe') {
    return (
      <div className="antwort-feld">
        <p>Tippe die richtige Position im Kreis an.</p>
        <Hilfehinweis aufgabe={aufgabe} modus={modus} onZeige={onHilfe} istVersteckt={istVersteckt} aktiviert={istHilfeGenutzt} />
      </div>
    )
  }

  if (spec.typ === 'tonika-spielen') {
    return (
      <div className="antwort-feld">
        <p>Drücke die passende Taste auf der Klaviatur.</p>
        <Hilfehinweis aufgabe={aufgabe} modus={modus} onZeige={onHilfe} istVersteckt={istVersteckt} aktiviert={istHilfeGenutzt} />
      </div>
    )
  }

  if (spec.typ === 'klang-erkennen-tonika') {
    return (
      <div className="antwort-feld">
        <button className="klang-spielen" onClick={() => kadenz(spec.tonart.tonicMidi)}>
          ♪ Kadenz erneut hören
        </button>
        <p>Tippe die erkannte Tonart im Quintenzirkel an.</p>
      </div>
    )
  }

  if (spec.typ === 'tonart-aus-anzahl' || spec.typ === 'dominante-finden' || spec.typ === 'subdominante-finden') {
    return (
      <div className="antwort-feld">
        <p>Wähle eine Tonart:</p>
        <div className="wahl-buttons">
          {DUR_TONARTEN.map(t => (
            <button key={t.name} disabled={feedback !== 'pending'} onClick={() => onAntwort(t.name)}>
              {t.name}
            </button>
          ))}
        </div>
        <Hilfehinweis aufgabe={aufgabe} modus={modus} onZeige={onHilfe} istVersteckt={istVersteckt} aktiviert={istHilfeGenutzt} />
      </div>
    )
  }

  if (spec.typ === 'vorzeichen-anzahl') {
    const optionen = [
      'keine Vorzeichen',
      ...[1, 2, 3, 4, 5, 6].map(n => `${n} Kreuz${n === 1 ? '' : 'e'} (♯)`),
      ...[1, 2, 3, 4, 5, 6].map(n => `${n} B${n === 1 ? '' : 's'} (♭)`),
    ]
    return (
      <div className="antwort-feld">
        <p>Wähle die Anzahl der Vorzeichen:</p>
        <div className="wahl-buttons schmal">
          {optionen.map(o => (
            <button key={o} disabled={feedback !== 'pending'} onClick={() => onAntwort(o)}>
              {o}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="antwort-feld">
      <label>
        Antwort:
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={feedback !== 'pending'}
          onKeyDown={(e) => { if (e.key === 'Enter') onAntwort(text) }}
        />
      </label>
      <button disabled={feedback !== 'pending'} onClick={() => onAntwort(text)}>Antworten</button>
      <Hilfehinweis aufgabe={aufgabe} modus={modus} onZeige={onHilfe} istVersteckt={istVersteckt} aktiviert={istHilfeGenutzt} />
    </div>
  )
}

interface HilfeProps {
  aufgabe: Aufgabe
  modus: Mode
  onZeige: () => void
  istVersteckt: boolean
  aktiviert: boolean
}

function Hilfehinweis({ aufgabe, modus, onZeige, istVersteckt, aktiviert }: HilfeProps) {
  if (modus === 'ueben-ohne-hilfe' && istVersteckt) return null
  if (aktiviert) {
    return <p className="hilfehinweis aktiviert">{tippText(aufgabe)}</p>
  }
  return (
    <button className="hilfe-button" onClick={onZeige}>
      Hilfe anzeigen (zählt als ausgenutzt)
    </button>
  )
}

function tippText(aufgabe: Aufgabe): string {
  const spec = aufgabe.spec
  if ('tonart' in spec) {
    return `Tipp: Position ${spec.tonart.fifths} im Quintenzirkel (12 Uhr = C, Uhrzeigersinn ♯, Gegenuhr ♭).`
  }
  if (spec.typ === 'tonart-aus-anzahl') {
    return spec.accidentalType === 'sharp'
      ? 'Tipp: Bei Kreuzen jede weitere Tonart eine Quinte höher (C → G → D → A → E → H → F♯).'
      : 'Tipp: Bei Bs jede weitere Tonart eine Quinte tiefer (C → F → B → Es → As → Des).'
  }
  return 'Tipp: Schaue auf den Quintenzirkel oben.'
}

interface StatProps {
  session: SessionEintrag[]
  karten: FsrsKarte[]
}

function SessionStatistik({ session, karten }: StatProps) {
  const richtig = session.filter(s => s.korrekt).length
  const total   = session.length
  const quote   = total === 0 ? 0 : Math.round(100 * richtig / total)
  return (
    <details className="session-statistik">
      <summary>Sitzungs-Statistik ({total} Aufgaben, {quote}% richtig)</summary>
      <ul className="karten-stand">
        {karten.length === 0 && <li>Noch keine Karten im Lernfortschritt.</li>}
        {karten.map(k => {
          const ua = findeUebungsabschnitt(k.uebungsabschnittId)
          return (
            <li key={k.id}>
              <strong>{ua?.name ?? k.id}</strong> — {k.state}, reps={k.reps}, lapses={k.lapses},
              {' '}Stabilität={k.stability.toFixed(2)}, fällig {new Date(k.due).toLocaleDateString('de-DE')}
            </li>
          )
        })}
      </ul>
    </details>
  )
}

